import {
	init,
	track,
	type PlausibleEventOptions,
} from '@plausible-analytics/tracker';

const PLAUSIBLE_ALLOWED_ORIGIN = 'https://ccr.imaaagery.app';
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN || 'ccr.imaaagery.app';
const PLAUSIBLE_ENABLED = process.env.REACT_APP_PLAUSIBLE_ENABLED === 'true';
const ANALYTICS_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';
const ANALYTICS_EVENT_DELAY_MS = 1000;
const EVENT_JS_NAME = 'clinical_reality_skin_diagnostic';
const PLAUSIBLE_PROJECT = 'clinicalreality';

type PlausibleWindow = Window & {
	__clinicalRealityPlausibleInitialized?: boolean;
};

type AnalyticsEventOptions = Omit<PlausibleEventOptions, 'props'> & {
	props?: Record<string, string | number | boolean | null | undefined>;
	sendEventJS?: boolean;
};

type AnalyticsEventConfig = AnalyticsEventOptions | false;

type EventJSPayload = {
	event_type: 'link';
	event_name: typeof EVENT_JS_NAME;
	event_category: string;
	event_action?: string;
	event_label?: string;
};

type QueuedAnalyticsEvent = {
	eventName: string;
	options: AnalyticsEventOptions;
	props: NonNullable<AnalyticsEventOptions['props']>;
	eventJSPayload: EventJSPayload;
	shouldSendEventJS: boolean;
};

const analyticsQueue: QueuedAnalyticsEvent[] = [];
let isProcessingAnalyticsQueue = false;

const isPlausibleAnalyticsAllowed = () => (
	typeof window !== 'undefined'
	&& window.location.origin === PLAUSIBLE_ALLOWED_ORIGIN
);

export const initializePlausibleAnalytics = () => {
	if (!PLAUSIBLE_ENABLED || !isPlausibleAnalyticsAllowed()) {
		return;
	}

	const plausibleWindow = window as PlausibleWindow;

	if (plausibleWindow.__clinicalRealityPlausibleInitialized) {
		return;
	}

	init({
		domain: PLAUSIBLE_DOMAIN,
		captureOnLocalhost: false,
		customProperties: {
			project: PLAUSIBLE_PROJECT,
		},
		logging: false,
	});

	plausibleWindow.__clinicalRealityPlausibleInitialized = true;
};

const processAnalyticsQueue = () => {
	if (isProcessingAnalyticsQueue) {
		return;
	}

	const queuedEvent = analyticsQueue.shift();
	if (!queuedEvent) {
		return;
	}

	isProcessingAnalyticsQueue = true;

	const {
		eventName,
		options,
		props,
		eventJSPayload,
		shouldSendEventJS,
	} = queuedEvent;

	try {
		if (ANALYTICS_DEBUG) {
			console.info(`Analytics event: ${eventName}`, {
				eventJS: shouldSendEventJS ? eventJSPayload : undefined,
				plausible: PLAUSIBLE_ENABLED ? { props } : undefined,
			});
		}

		if (shouldSendEventJS) {
			window.parent.postMessage({
				_elc_action: 'analyticsIframeEvent',
				payload: eventJSPayload,
			}, '*');
		}

		if (PLAUSIBLE_ENABLED) {
			track(eventName, {
				interactive: options.interactive,
				revenue: options.revenue,
				props: props as PlausibleEventOptions['props'],
				callback: (result) => {
					if (!result && ANALYTICS_DEBUG) {
						console.warn(`Ignoring event: ${eventName}`, { props });
					}

					options.callback?.(result);
				},
			});
		}
	} catch (error) {
		options.callback?.({ error });
		if (ANALYTICS_DEBUG) {
			console.error(`Failed to track event: ${eventName}`, { error, props });
		}
	}

	window.setTimeout(() => {
		isProcessingAnalyticsQueue = false;
		processAnalyticsQueue();
	}, ANALYTICS_EVENT_DELAY_MS);
};

const buildEventJSPayload = (
	eventName: string,
	props: AnalyticsEventOptions['props'] = {}
): EventJSPayload => {
	const payload: EventJSPayload = {
		event_type: 'link',
		event_name: EVENT_JS_NAME,
		event_category: eventName,
	};

	if (eventName === 'sd_concern_brand_ranking') {
		if (props.concern !== undefined && props.concern !== null) {
			payload.event_action = String(props.concern);
		}

		if (props.rank !== undefined && props.rank !== null) {
			payload.event_label = `rank_${props.rank}`;
		}

		return payload;
	}

	const eventProp = Object.entries(props)
		.find(([, value]) => value !== undefined && value !== null);

	if (eventProp) {
		const [eventAction, eventLabel] = eventProp;
		payload.event_action = eventAction;
		payload.event_label = String(eventLabel);
	}

	return payload;
};

export const trackPlausibleEvent = (
	eventName: string,
	config: AnalyticsEventConfig = {},
	sendEventJS?: boolean
) => {
	if (!isPlausibleAnalyticsAllowed()) {
		return;
	}

	initializePlausibleAnalytics();

	const options = config === false ? {} : config;

	const props = {
		...options.props,
		project: PLAUSIBLE_PROJECT,
	};

	analyticsQueue.push({
		eventName,
		options,
		props,
		eventJSPayload: buildEventJSPayload(eventName, options.props),
		shouldSendEventJS: config !== false
			&& (sendEventJS ?? options.sendEventJS ?? true),
	});

	processAnalyticsQueue();
};
